/**
 * Playbooks (Rutinas) list for a single patient profile.
 * Reached via /professional/profiles/:profileId/playbooks
 * Profile object is passed via location state from ProfessionalHub.
 */
import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { FiPlay, FiEdit2, FiTrash2 } from 'react-icons/fi'
import { useTranslation } from '../i18n/LanguageContext'
import { useAuth } from '../contexts/AuthContext'
import { professional as api, type PlaybookData, type ProfessionalProfile } from '../api/client'
import PageHeader from '../components/PageHeader'

export default function ProfessionalPlaybooks() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { profileId } = useParams<{ profileId: string }>()
  const { t }     = useTranslation()
  const { user }  = useAuth()
  const p         = t.professional

  // Profile passed via navigate state (avoids an extra fetch)
  const profile = (location.state as { profile?: ProfessionalProfile } | null)?.profile

  const [playbooks, setPlaybooks]       = useState<PlaybookData[]>([])
  const [loading, setLoading]           = useState(true)
  const [showForm, setShowForm]         = useState(false)
  const [formName, setFormName]         = useState('')
  const [saving, setSaving]             = useState(false)
  const [starting, setStarting]         = useState<string | null>(null)

  // Guard: must be premium + professional
  const isPremium      = user?.tier === 'PREMIUM'
  const isProfessional = user?.isProfessional ?? false

  useEffect(() => {
    if (!profileId || !isPremium || !isProfessional) return
    api.listPlaybooks(profileId)
      .then(setPlaybooks)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [profileId, isPremium, isProfessional])

  if (!isPremium || !isProfessional) {
    navigate('/professional', { replace: true })
    return null
  }

  const createPlaybook = async () => {
    if (!formName || !profileId) return
    setSaving(true)
    try {
      const pb = await api.createPlaybook(profileId, { name: formName })
      setPlaybooks(prev => [...prev, pb])
      setShowForm(false)
      setFormName('')
      // Go straight into editor for the new playbook
      navigate(
        `/professional/profiles/${profileId}/playbooks/${pb.id}/edit`,
        { state: { playbook: pb, profile } },
      )
    } catch {
      // ignore
    } finally {
      setSaving(false)
    }
  }

  const deletePlaybook = async (id: string) => {
    await api.deletePlaybook(id).catch(() => {})
    setPlaybooks(prev => prev.filter(pb => pb.id !== id))
  }

  const startSession = async (pb: PlaybookData) => {
    setStarting(pb.id)
    try {
      const session = await api.startSession(pb.id)
      navigate(
        `/professional/profiles/${profileId}/playbooks/${pb.id}/session`,
        { state: { session, items: session.playbookItems ?? pb.items, profile } },
      )
    } catch {
      // ignore
    } finally {
      setStarting(null)
    }
  }

  return (
    <div style={s.root}>
      <PageHeader
        onBack={() => navigate('/professional')}
        backLabel={p.back}
        title={`${profile?.name ?? '…'} — ${p.playbooksTitle}`}
      />
      <div style={s.inner}>
        <button style={s.addBtn} onClick={() => { setFormName(''); setShowForm(true) }}>
          + {p.newPlaybook}
        </button>

        {loading && <p style={s.hint}>…</p>}
        {!loading && playbooks.length === 0 && <p style={s.hint}>{p.noPlaybooks}</p>}

        {playbooks.map(pb => (
          <div key={pb.id} style={s.card}>
            <div style={s.cardMain}>
              <span style={s.cardEmoji}>📋</span>
              <div style={s.cardInfo}>
                <div style={s.cardName}>{pb.name}</div>
                <div style={s.cardNote}>{pb.items.length} actividades</div>
              </div>
            </div>
            <div style={s.cardActions}>
              <button
                style={{ ...s.actionBtn, ...(pb.items.length === 0 || starting === pb.id ? s.actionBtnDisabled : {}) }}
                onClick={() => startSession(pb)}
                disabled={pb.items.length === 0 || !!starting}
                aria-label="Iniciar sesión"
              >
                <FiPlay size={16} />
              </button>
              <button
                style={s.actionBtn}
                onClick={() => navigate(
                  `/professional/profiles/${profileId}/playbooks/${pb.id}/edit`,
                  { state: { playbook: pb, profile } },
                )}
                aria-label="Editar"
              >
                <FiEdit2 size={16} />
              </button>
              <button
                style={{ ...s.actionBtn, ...s.actionBtnDanger }}
                onClick={() => deletePlaybook(pb.id)}
                aria-label="Eliminar"
              >
                <FiTrash2 size={16} />
              </button>
            </div>
          </div>
        ))}

        {/* Create playbook modal */}
        {showForm && (
          <div style={s.overlay}>
            <div style={s.modal}>
              <h2 style={s.modalTitle}>{p.createPlaybook}</h2>
              <label style={s.fieldLabel}>{p.playbookName}</label>
              <input
                style={s.input}
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder={p.playbookName}
                maxLength={120}
                autoFocus
              />
              <div style={s.modalActions}>
                <button style={s.ghostBtn} onClick={() => setShowForm(false)}>{p.cancel}</button>
                <button
                  style={{ ...s.primaryBtn, ...(saving || !formName ? s.primaryBtnDisabled : {}) }}
                  onClick={createPlaybook}
                  disabled={saving || !formName}
                >
                  {saving ? '…' : p.savePlaybook}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:  '100vh',
    background: '#FAF6EF',
  },
  inner: {
    maxWidth: '640px',
    margin:   '0 auto',
    padding:  '24px 16px 40px',
    display:  'flex',
    flexDirection: 'column',
    gap:      '16px',
  },
  hint: {
    fontSize: '1rem',
    color:     '#888',
    textAlign: 'center',
    padding:   '16px 0',
  },
  addBtn: {
    background:   '#EAF3EB',
    border:       '2px dashed #5B8A5F',
    borderRadius: '14px',
    padding:      '14px',
    fontSize: '1.0625rem',
    fontWeight:   600,
    color:        '#2D5A32',
    cursor:       'pointer',
    minHeight:    '56px',
  },
  card: {
    background:   '#fff',
    borderRadius: '16px',
    border:       '1px solid #e8e0d5',
    padding:      '14px 16px',
    display:      'flex',
    alignItems:   'center',
    gap:          '12px',
  },
  cardMain: {
    display:    'flex',
    alignItems: 'center',
    gap:        '10px',
    flex:       1,
    minWidth:   0,
  },
  cardEmoji: {
    fontSize: '1.5rem',
    flexShrink: 0,
  },
  cardInfo: {
    flex:     1,
    minWidth: 0,
  },
  cardName: {
    fontSize: '1.0625rem',
    fontWeight:   600,
    color:        '#2D3A2E',
    overflow:     'hidden',
    textOverflow: 'ellipsis',
    whiteSpace:   'nowrap',
  },
  cardNote: {
    fontSize: '0.875rem',
    color:     '#888',
    marginTop: '2px',
  },
  cardActions: {
    display:    'flex',
    gap:        '6px',
    flexShrink: 0,
  },
  actionBtn: {
    background:     'none',
    border:         '1px solid #e0d8cf',
    borderRadius:   '10px',
    padding:        '6px 10px',
    cursor:         'pointer',
    minHeight:      '40px',
    minWidth:       '40px',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    color:          '#555',
  },
  actionBtnDisabled: {
    opacity: 0.4,
    cursor:  'not-allowed',
  },
  actionBtnDanger: {
    borderColor: '#f5c6c2',
    color:       '#c0392b',
  },
  primaryBtn: {
    background:   '#5B8A5F',
    color:        '#fff',
    border:       'none',
    borderRadius: '12px',
    padding:      '12px 24px',
    fontSize: '1rem',
    fontWeight:   700,
    cursor:       'pointer',
    minHeight:    '48px',
  },
  primaryBtnDisabled: {
    opacity: 0.6,
    cursor:  'not-allowed',
  },
  ghostBtn: {
    background:   'none',
    color:        '#666',
    border:       '1px solid #ddd',
    borderRadius: '12px',
    padding:      '12px 24px',
    fontSize: '1rem',
    cursor:       'pointer',
    minHeight:    '48px',
  },
  overlay: {
    position:       'fixed',
    inset:          0,
    background:     'rgba(0,0,0,0.5)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    zIndex:         999,
    padding:        '16px',
  },
  modal: {
    background:    '#fff',
    borderRadius:  '20px',
    padding:       '28px 24px',
    maxWidth:      '400px',
    width:         '100%',
    display:       'flex',
    flexDirection: 'column',
    gap:           '12px',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 800,
    color:      '#2D3A2E',
    margin:     0,
  },
  modalActions: {
    display:        'flex',
    justifyContent: 'flex-end',
    gap:            '10px',
    marginTop:      '4px',
  },
  fieldLabel: {
    fontSize: '0.875rem',
    fontWeight:   600,
    color:        '#444',
    marginBottom: '-4px',
  },
  input: {
    fontSize: '1rem',
    padding:      '12px',
    borderRadius: '10px',
    border:       '1px solid #ddd',
    background:   '#FAF6EF',
    width:        '100%',
    boxSizing:    'border-box',
    minHeight:    '48px',
  },
}
